import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  alpha,
  useTheme,
  FormHelperText,
  styled,
} from "@mui/material";
import {
  AccountBalance as BankIcon,
  BusinessCenter as BusinessIcon,
  LocationOn as LocationIcon,
  Money as MoneyIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { showToast } from "../../components/common/Util/Util";
import OTPVerification from "../borrowers/OTPVerification";
import {
  createLender,
  getRegion,
  sendOtpFromAdmin,
} from "../../services/userService";
import { useSelector } from "react-redux";
import { ip, coordinates } from "../../Store/userSlice";

const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
  paddingLeft: theme.spacing(1),
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: 3,
    height: "70%",
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: '16.5px 14px',
    minWidth: '300px'
  },
  '& .MuiOutlinedInput-root': {
    minWidth: '300px'
  }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: '300px',
  flex: 1,
  '& .MuiInputBase-root': {
    minWidth: '300px'
  },
  '& .MuiOutlinedInput-root': {
    minWidth: '300px'
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  gap: theme.spacing(1.5),
  '& .MuiFormControl-root': {
    minWidth: '300px',
    flex: 1
  },
  '& .MuiOutlinedInput-root': {
    minWidth: '300px'
  }
}));

const LenderOnboardingForm = ({ initialData, onVerificationStart }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    mobileNumber: initialData?.mobileNumber || "",
    email: initialData?.email || "",
    name: initialData?.name || "",
    financialInstitutionType: "",
    financialInstitution: "",
    location: "",
    typeOfLending: "",
    typeOfRetailLoans: "",
    designation: "",
  });

  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [otpRequestData, setOtpRequestData] = useState(null);
  const [regions, setRegions] = useState([]);
  const [errors, setErrors] = useState({});
  const IpAddress = useSelector(ip);
  const userCoordinates = useSelector(coordinates);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await getRegion();
        if (response?.result) {
          setRegions(response.result);
        }
      } catch (error) {
        showToast("Failed to load locations", "error");
      }
    };
    fetchRegions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === "mobileNumber") {
      const digitsOnly = value.replace(/\D/g, "");
      nextValue = digitsOnly.slice(0, 10);
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: nextValue,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = "Name is required";

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (
      !/^(\+91-?)?[0-9]{10}$/.test(formData.mobileNumber.replace(/\s+/g, ""))
    ) {
      newErrors.mobileNumber =
        "Invalid mobile number format (should be 10 digits with optional +91 prefix)";
    }

    if (!formData.financialInstitutionType)
      newErrors.financialInstitutionType =
        "Financial institution type is required";
    if (!formData.financialInstitution)
      newErrors.financialInstitution = "Financial institution name is required";
    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.typeOfLending)
      newErrors.typeOfLending = "Type of lending is required";
    if (!formData.typeOfRetailLoans)
      newErrors.typeOfRetailLoans = "Type of retail loans is required";
    if (!formData.designation)
      newErrors.designation = "Designation is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOTPVerificationSuccess = async () => {
    try {
      setLoading(true);
      setShowOTPVerification(false);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const isSuperAdmin = user.role === "SUPERADMIN";
      const registrationData = {
        ip: IpAddress,
        longitude: userCoordinates?.lng || "",
        latitude: userCoordinates?.lat || "",
        uuid: user.uuid || "",
        name: formData.name,
        mobileNumber: formData.mobileNumber,
        email: formData.email,
        userType: "lender",
        screen: "create",
        address: formData.location,
        lender: {
          financialInstitutionType: formData.financialInstitutionType,
          financialInstitution: formData.financialInstitution,
          typeOfLending: formData.typeOfLending,
          typeOfRetailLoans: formData.typeOfRetailLoans,
          designation: formData.designation,
          location: formData.location,
          verified: isSuperAdmin,
        },
      };
      createLender(registrationData)
        .then(async (data) => {
          if (data?.statusCode === 200) {
            if (
              data.message === "Email is already used for another account." ||
              data.message ===
                "Phone number is already used for another account."
            ) {
              throw new Error(data.message);
            }

            showToast("Lender registered successfully", "success");
            setIsSubmitted(true);
            if (onVerificationStart && data?.result) {
              const lenderId = data.result.lenderId || data.result.id || null;
              const userId = data.result.ID || data.result.id || null;

              if (lenderId && userId) {
                onVerificationStart(lenderId, formData, userId);
              } else {
                showToast(
                  "Registration successful but some data was missing. Please contact support.",
                  "warning"
                );
              }
            }
          } else {
            throw new Error(data?.message || "Registration failed");
          }
        })
        .catch((error) => {
          if (!error.message?.includes("already used")) {
            showToast(error.message || "Registration failed", "error");
          } else {
            showToast(error.message, "error");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error("Error during OTP verification:", error);
    }
  };

  const handleSubmit = async () => {
    if (loading || isSubmitted) return;
    if (!validateForm()) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    setLoading(true);
    let isSuperAdmin = false;
    let shouldResetLoading = true;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      isSuperAdmin = user.role === "SUPERADMIN";

      if (isSuperAdmin) {
        shouldResetLoading = false;
        handleOTPVerificationSuccess();
        // const registrationData = {
        //   ip: IpAddress,
        //   longitude: "string",
        //   latitude: "string",
        //   uuid: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
        //   name: formData.name,
        //   mobileNumber: formData.mobileNumber,
        //   email: formData.email,
        //   userType: "lender",
        //   screen: "create",
        //   address: formData.location,
        //   lender: {
        //     financialInstitutionType: formData.financialInstitutionType,
        //     financialInstitution: formData.financialInstitution,
        //     typeOfLending: formData.typeOfLending,
        //     typeOfRetailLoans: formData.typeOfRetailLoans,
        //     designation: formData.designation,
        //     location: formData.location,
        //     verified: true,
        //   },
        // };
        // createLender(registrationData)
        //   .then(async (data) => {
        //     if (data?.statusCode === 200) {
        //       if (
        //         data.message === "Email is already used for another account." ||
        //         data.message ===
        //           "Phone number is already used for another account."
        //       ) {
        //         showToast(data.message, "error");
        //         throw new Error(data.message);
        //       }

        //       showToast("Lender registered successfully", "success");
        //       if (onVerificationStart && data?.result) {
        //         const lenderId = data.result.lenderId || data.result.id || null;
        //         const userId = data.result.ID || data.result.id || null;

        //         if (lenderId && userId) {
        //           onVerificationStart(lenderId, formData, userId);
        //         } else {
        //           showToast(
        //             "Registration successful but some data was missing. Please contact support.",
        //             "warning"
        //           );
        //         }
        //       }
        //     } else {
        //       throw new Error(data?.message || "Registration failed");
        //     }
        //   })
        //   .catch((error) => {})
        //   .finally(() => {
        //     setLoading(false);
        //   });
      } else {
        // For non-superadmin users, send OTP and show verification
        const otpRequestData = {
          mobileNumber: formData.mobileNumber,
          email: formData.email,
        };
        sendOtpFromAdmin(otpRequestData)
          .then(async (otpData) => {
            if (otpData?.responseCode === 200) {
              if (
                otpData.responseMessage ===
                  "Email is already used for another account." ||
                otpData.responseMessage ===
                  "Phone number is already used for another account."
              ) {
                throw new Error(otpData.responseMessage);
              }

              showToast("OTP sent successfully", "success");
              setOtpRequestData({
                mobileNumber: formData.mobileNumber,
                email: formData.email,
                ip: IpAddress,
                longitude: userCoordinates?.lng || "",
                latitude: userCoordinates?.lat || "",
              });
              setShowOTPVerification(true);
            } else {
              throw new Error(otpData?.responseMessage || "Failed to send OTP");
            }
          })
          .catch((error) => {
            if (!error.message?.includes("already used")) {
              showToast(error.message || "Registration failed", "error");
            } else {
              showToast(error.message, "error");
            }
          });
      }
    } catch (error) {
      if (!error.message?.includes("already used")) {
        showToast(error.message || "Registration failed", "error");
      } else {
      }
    } finally {
      if (shouldResetLoading) setLoading(false);
    }
  };

  const handleOTPVerificationError = (message) => {
    if (!message?.includes("already used")) {
      showToast(message || "OTP verification failed", "error");
    } else {
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <FormSectionTitle>Basic Information</FormSectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    mr: 1.5,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                  }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
                <FormControl fullWidth error={!!errors.name}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.main,
                    mr: 1.5,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                  }}
                >
                  <PhoneIcon fontSize="small" />
                </Avatar>
                <FormControl fullWidth error={!!errors.mobileNumber}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                    placeholder="+91-XXXXXXXXXX"
                    error={!!errors.mobileNumber}
                    helperText={
                      errors.mobileNumber ||
                      "Enter a valid mobile number for verification"
                    }
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.main,
                    mr: 1.5,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                  }}
                >
                  <EmailIcon fontSize="small" />
                </Avatar>
                <FormControl fullWidth error={!!errors.email}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={
                      errors.email ||
                      "Enter your email address for verification"
                    }
                  />
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <FormSectionTitle>Organization Details</FormSectionTitle>
          <Grid 
            container 
            spacing={3} 
            sx={{
              width: '100%',
              margin: 0,
              padding: 0,
              '& .MuiGrid-item': {
                paddingTop: '24px',
                paddingLeft: '24px',
                display: 'flex'
              }
            }}
          >
            <Grid item xs={12} md={6}>
              <StyledBox>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.main,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                    flexShrink: 0
                  }}
                >
                  <BankIcon fontSize="small" />
                </Avatar>
                <StyledFormControl error={!!errors.financialInstitutionType}>
                  <InputLabel>Financial Institution Type</InputLabel>
                  <StyledSelect
                    name="financialInstitutionType"
                    value={formData.financialInstitutionType}
                    onChange={handleChange}
                    label="Financial Institution Type"
                  >
                    <MenuItem value="BANK">Bank</MenuItem>
                    <MenuItem value="NBFC">NBFC</MenuItem>
                    <MenuItem value="CREDIT_UNION">Credit Union</MenuItem>
                    <MenuItem value="PRIVATE">Private Lender</MenuItem>
                    <MenuItem value="GOVERNMENT">Government</MenuItem>
                  </StyledSelect>
                  {errors.financialInstitutionType && (
                    <FormHelperText>
                      {errors.financialInstitutionType}
                    </FormHelperText>
                  )}
                </StyledFormControl>
              </StyledBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledBox>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.12),
                    color: theme.palette.secondary.main,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                    flexShrink: 0
                  }}
                >
                  <BusinessIcon fontSize="small" />
                </Avatar>
                <StyledFormControl error={!!errors.financialInstitution}>
                  <TextField
                    fullWidth
                    label="Financial Institution Name"
                    name="financialInstitution"
                    value={formData.financialInstitution}
                    onChange={handleChange}
                    error={!!errors.financialInstitution}
                    helperText={errors.financialInstitution}
                  />
                </StyledFormControl>
              </StyledBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledBox>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    color: theme.palette.error.main,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                    flexShrink: 0
                  }}
                >
                  <LocationIcon fontSize="small" />
                </Avatar>
                <StyledFormControl error={!!errors.location}>
                  <InputLabel>Location</InputLabel>
                  <StyledSelect
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    label="Location"
                  >
                    {Array.isArray(regions) &&
                      regions.map((region) => (
                        <MenuItem key={region} value={region}>
                          {region}
                        </MenuItem>
                      ))}
                  </StyledSelect>
                  {errors.location && (
                    <FormHelperText>{errors.location}</FormHelperText>
                  )}
                </StyledFormControl>
              </StyledBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledBox>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: theme.palette.warning.main,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                    flexShrink: 0
                  }}
                >
                  <WorkIcon fontSize="small" />
                </Avatar>
                <StyledFormControl error={!!errors.designation}>
                  <TextField
                    fullWidth
                    label="Designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    error={!!errors.designation}
                    helperText={errors.designation}
                  />
                </StyledFormControl>
              </StyledBox>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <FormSectionTitle>Lending Details</FormSectionTitle>
          <Grid 
            container 
            spacing={3} 
            sx={{
              width: '100%',
              margin: 0,
              padding: 0,
              '& .MuiGrid-item': {
                paddingTop: '24px',
                paddingLeft: '24px',
                display: 'flex'
              }
            }}
          >
            <Grid item xs={12} md={6}>
              <StyledBox>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                    flexShrink: 0
                  }}
                >
                  <MoneyIcon fontSize="small" />
                </Avatar>
                <StyledFormControl error={!!errors.typeOfLending}>
                  <InputLabel>Type of Lending</InputLabel>
                  <StyledSelect
                    name="typeOfLending"
                    value={formData.typeOfLending}
                    onChange={handleChange}
                    label="Type of Lending"
                  >
                    <MenuItem value="SECURED">Secured</MenuItem>
                    <MenuItem value="UNSECURED">Unsecured</MenuItem>
                    <MenuItem value="BOTH">Both</MenuItem>
                  </StyledSelect>
                  {errors.typeOfLending && (
                    <FormHelperText>{errors.typeOfLending}</FormHelperText>
                  )}
                </StyledFormControl>
              </StyledBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledBox>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.12),
                    color: theme.palette.secondary.main,
                    mt: 1.5,
                    width: 28,
                    height: 28,
                    flexShrink: 0
                  }}
                >
                  <MoneyIcon fontSize="small" />
                </Avatar>
                <StyledFormControl error={!!errors.typeOfRetailLoans}>
                  <InputLabel>Type of Retail Loans</InputLabel>
                  <StyledSelect
                    name="typeOfRetailLoans"
                    value={formData.typeOfRetailLoans}
                    onChange={handleChange}
                    label="Type of Retail Loans"
                  >
                    <MenuItem value="PERSONAL">Personal Loan</MenuItem>
                    <MenuItem value="BUSINESS">Business Loan</MenuItem>
                    <MenuItem value="BOTH">Both</MenuItem>
                  </StyledSelect>
                  {errors.typeOfRetailLoans && (
                    <FormHelperText>{errors.typeOfRetailLoans}</FormHelperText>
                  )}
                </StyledFormControl>
              </StyledBox>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || isSubmitted}
          sx={{
            px: 4,
            py: 1,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: (theme) =>
              `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
            transition: "all 0.2s ease",
            "&:hover": {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              transform: "translateY(-1px)",
              boxShadow: (theme) =>
                `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&.Mui-disabled": {
              background: (theme) => alpha(theme.palette.action.disabled, 0.12),
            },
          }}
        >
          {loading
            ? "Submitting..."
            : JSON.parse(localStorage.getItem("user")).role === "SUPERADMIN"
            ? "Add Lender"
            : "Next"}
        </Button>
      </Box>

      <OTPVerification
        showOTPVerification={showOTPVerification}
        setShowOTPVerification={setShowOTPVerification}
        showMobileOTP={true}
        requestData={otpRequestData}
        onVerificationSuccess={handleOTPVerificationSuccess}
        onVerificationError={handleOTPVerificationError}
      />
    </Box>
  );
};

export default LenderOnboardingForm;
